// MessengerPress Phase 2 — top-level admin page (issue #2392).
// Three-panel layout:
//   • SourcePdfPicker  (left)   — pick a recently-generated PDF
//   • RecipeBuilder    (right)  — preset + JSON editor + submit
//   • JobHistoryList   (bottom) — last 50 jobs, polls until terminal
//
// Gated by useIsAdmin — non-admins are redirected to `/`. Lives under
// the admin route prefix at /admin/messenger-press alongside the other
// admin tools (analytics, assets, themes, mio-report, etc.).

import { useState } from 'react'
import { Redirect } from 'react-router-dom'
import { useRecoilValue } from 'recoil'
import styled from 'styled-components'
import colors from 'utils/styling/colors'
import { userAtom } from '@/atoms/_globalAtoms'
import { useIsAdmin } from '@/hooks/utility/useIsAdmin'
import { Navigation } from 'components'
import {
  type MessengerPressJob,
  type MessengerPressSourcePdf,
} from '@/api/messenger-press'
import { JobHistoryList } from './JobHistoryList'
import { RecipeBuilder } from './RecipeBuilder'
import { SourcePdfPicker } from './SourcePdfPicker'

// Inline data-testid constants — kept in lockstep with the central
// registry at apps/mdl-frontend/tests/utils/selectors.ts.
const TID_PAGE = 'messenger-press-page'

export const MessengerPressPage = () => {
  const isAdmin = useIsAdmin()
  // userAtom is hydrated asynchronously by Auth0.tsx after the /v1/user
  // fetch resolves. On a hard reload (bookmark, browser refresh, direct URL)
  // the first render happens BEFORE that fetch completes — userAtom is
  // null and useIsAdmin() returns false even for real super admins. Without
  // this loading guard the page would fire a one-shot <Redirect to="/" />
  // and the user would never make it to the admin tool. We hold off the
  // gate until the user atom is populated so the admin check is meaningful.
  const user = useRecoilValue(userAtom)

  // Currently picked source PDF — feeds the recipe builder so it can
  // auto-fill the matching preset.
  const [selectedPdf, setSelectedPdf] =
    useState<MessengerPressSourcePdf | null>(null)

  // Bumped after every successful POST to /v1/messenger-press so the job
  // history list refreshes immediately.
  const [historyRefreshKey, setHistoryRefreshKey] = useState(0)

  // Defer the admin gate until the user atom hydrates — see the long
  // comment above. `null` means "still loading", not "not an admin".
  if (user === null) {
    return null
  }
  if (!isAdmin) {
    return <Redirect to="/" />
  }

  const handleSelectPdf = (pdf: MessengerPressSourcePdf) => {
    setSelectedPdf(pdf)
  }

  const handleJobCreated = (_job: MessengerPressJob) => {
    setHistoryRefreshKey((n) => n + 1)
  }

  return (
    <>
      <Navigation type="admin" />
      <PageWrap data-testid={TID_PAGE}>
        <Header>
          <Title>MessengerPress</Title>
          <Subtitle>
            Admin tooling for the new messenger-press print pipeline. Pick a
            source PDF, tweak the recipe, and run.
          </Subtitle>
        </Header>

        <TopGrid>
          <SourcePdfPicker
            selectedPdfId={selectedPdf?.id}
            onSelect={handleSelectPdf}
          />
          <RecipeBuilder
            selectedPdfId={selectedPdf?.id}
            selectedPdfUrl={selectedPdf?.url}
            selectedPdfProductId={selectedPdf?.productId}
            selectedPdfLabel={selectedPdf?.caseName ?? selectedPdf?.productName}
            onJobCreated={handleJobCreated}
          />
        </TopGrid>

        <JobHistoryList refreshKey={historyRefreshKey} />
      </PageWrap>
    </>
  )
}

export default MessengerPressPage

const PageWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 32px 32px 80px 32px;
  max-width: 1400px;
  margin: 0 auto;
`

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const Title = styled.h1`
  font-size: 28px;
  font-weight: 700;
  margin: 0;
  color: ${colors('almostBlack')};
`

const Subtitle = styled.p`
  font-size: 14px;
  margin: 0;
  color: ${colors('gray2')};
`

const TopGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(320px, 1fr) minmax(360px, 1.4fr);
  gap: 24px;
  /* Cap the row height so both panels share the same envelope and scroll
     internally. Sized for the source PDF list with ~10 visible rows
     (each row is ~76px + search input + pager + headers ≈ 880px). The
     min-height keeps the layout sensible on very short viewports. */
  height: 880px;
  max-height: calc(100vh - 220px);
  min-height: 540px;
  align-items: stretch;

  /* Both children need their own scroll context. */
  > * {
    min-height: 0;
    max-height: 100%;
    overflow: hidden;
  }

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    height: auto;
    max-height: none;
  }
`
